"use client";

import { useMemo, useState } from "react";
import { AlertTriangle, Palette, Play, Rows3, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ClearButton } from "@/components/tools/clear-button";
import { CopyButton } from "@/components/tools/copy-button";
import { ErrorMessage } from "@/components/tools/error-message";
import { InputOutputPanel } from "@/components/tools/input-output-panel";
import { CodeOutput } from "@/components/tools/tool-output";
import { encodeBase64Unicode, decodeBase64Unicode } from "@/lib/tools/base64";
import { convertColor, type ColorConversion } from "@/lib/tools/color";
import { generateHashes, type HashOutput } from "@/lib/tools/hash";
import { escapeHtml, unescapeHtml } from "@/lib/tools/html";
import { decodeJwt, type DecodedJwt } from "@/lib/tools/jwt";
import { formatJson, minifyJson, yamlToJson } from "@/lib/tools/json";
import { generatePassword } from "@/lib/tools/password";
import {
  decodeUrlText,
  encodeUrlText,
  parseUrl,
  type ParsedUrl,
  type UrlEncodingMode,
} from "@/lib/tools/url";
import { generateUuidBatch } from "@/lib/tools/uuid";
import { cn } from "@/lib/utils";

const jsonExample = `{
  "product": "LocalTools",
  "localOnly": true,
  "tools": ["json", "jwt", "utm"]
}`;

const yamlExample = `product: LocalTools
localOnly: true
tools:
  - json
  - jwt
  - utm
`;

const jwtExample =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkxvY2FsVG9vbHMgRGVtbyIsImlhdCI6MTUxNjIzOTAyMn0.signature";

type UrlEncodingAction = "encode" | "decode";

function getUrlEncodingExample(action: UrlEncodingAction, mode: UrlEncodingMode) {
  if (mode === "full-url") {
    return action === "encode"
      ? "https://example.com/search?q=hello world&tag=tools"
      : "https://example.com/search?q=hello%20world&tag=tools";
  }

  return action === "encode"
    ? "launch plan & budget=small"
    : "launch%20plan%20%26%20budget%3Dsmall";
}

function runTextTransform(
  input: string,
  transformer: (value: string) => ReturnType<typeof formatJson>,
) {
  if (!input.trim()) {
    return {
      output: "",
      error: "Paste some input first.",
    };
  }

  const result = transformer(input);

  return result.ok
    ? { output: result.value, error: "" }
    : { output: "", error: result.error };
}

type JsonToolProps = {
  mode: "format" | "minify" | "yaml";
};

export function JsonTool({ mode }: JsonToolProps) {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");
  const isYaml = mode === "yaml";
  const title =
    mode === "format" ? "Raw JSON" : mode === "minify" ? "JSON document" : "YAML document";
  const outputTitle =
    mode === "format" ? "Formatted JSON" : mode === "minify" ? "Minified JSON" : "JSON output";

  const process = () => {
    const transformer =
      mode === "format" ? formatJson : mode === "minify" ? minifyJson : yamlToJson;
    const result = runTextTransform(input, transformer);
    setOutput(result.output);
    setError(result.error);
  };

  return (
    <InputOutputPanel
      inputTitle={title}
      outputTitle={outputTitle}
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setInput(isYaml ? yamlExample : jsonExample)}
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton
            onClear={() => {
              setInput("");
              setOutput("");
              setError("");
            }}
          />
        </>
      }
      outputActions={<CopyButton value={output} />}
      input={
        <div className="space-y-3">
          <Textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder={isYaml ? "Paste YAML here..." : "Paste JSON here..."}
            className="min-h-80"
          />
          <ErrorMessage message={error} />
          <Button type="button" onClick={process}>
            <Play className="h-4 w-4" />
            {mode === "format" ? "Format JSON" : mode === "minify" ? "Minify JSON" : "Convert"}
          </Button>
        </div>
      }
      output={<CodeOutput value={output} />}
    />
  );
}

export function Base64Tool() {
  const [mode, setMode] = useState<"encode" | "decode">("encode");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");

  const process = () => {
    if (mode === "encode") {
      setOutput(encodeBase64Unicode(input));
      setError("");
      return;
    }

    const result = decodeBase64Unicode(input);
    setOutput(result.ok ? result.value : "");
    setError(result.ok ? "" : result.error);
  };

  return (
    <InputOutputPanel
      inputTitle="Base64 text"
      outputTitle={mode === "encode" ? "Base64" : "Decoded text"}
      inputActions={
        <>
          <div className="inline-flex rounded-md border border-zinc-200 bg-white p-1">
            {(["encode", "decode"] as const).map((option) => (
              <Button
                key={option}
                type="button"
                size="sm"
                variant={mode === option ? "default" : "ghost"}
                onClick={() => {
                  setMode(option);
                  setOutput("");
                  setError("");
                }}
              >
                {option === "encode" ? "Encode" : "Decode"}
              </Button>
            ))}
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              setInput(mode === "encode" ? "Hello, Bucuresti" : "SGVsbG8sIEJ1Y3VyZXN0aQ==")
            }
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton
            onClear={() => {
              setInput("");
              setOutput("");
              setError("");
            }}
          />
        </>
      }
      outputActions={<CopyButton value={output} />}
      input={
        <div className="space-y-3">
          <Textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder={mode === "encode" ? "Type text to encode..." : "Paste Base64..."}
            className="min-h-72"
          />
          <ErrorMessage message={error} />
          <Button type="button" onClick={process}>
            <Play className="h-4 w-4" />
            {mode === "encode" ? "Encode" : "Decode"}
          </Button>
        </div>
      }
      output={<CodeOutput value={output} />}
    />
  );
}

export function HtmlEscapeTool() {
  const [mode, setMode] = useState<"escape" | "unescape">("escape");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");

  const process = () => {
    if (mode === "escape") {
      setOutput(escapeHtml(input));
      setError("");
      return;
    }

    const result = unescapeHtml(input);
    setOutput(result.ok ? result.value : "");
    setError(result.ok ? "" : result.error);
  };

  return (
    <InputOutputPanel
      inputTitle={mode === "escape" ? "HTML or text" : "Escaped HTML"}
      outputTitle={mode === "escape" ? "Escaped entities" : "Unescaped HTML"}
      inputActions={
        <>
          <div className="inline-flex rounded-md border border-zinc-200 bg-white p-1">
            {(["escape", "unescape"] as const).map((option) => (
              <Button
                key={option}
                type="button"
                size="sm"
                variant={mode === option ? "default" : "ghost"}
                onClick={() => {
                  setMode(option);
                  setOutput("");
                  setError("");
                }}
              >
                {option === "escape" ? "Escape" : "Unescape"}
              </Button>
            ))}
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              setInput(
                mode === "escape"
                  ? '<a href="/search?q=tools&sort=asc">Save & close</a>'
                  : "&lt;a href=&quot;/search?q=tools&amp;sort=asc&quot;&gt;Save &amp; close&lt;/a&gt;",
              )
            }
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton
            onClear={() => {
              setInput("");
              setOutput("");
              setError("");
            }}
          />
        </>
      }
      outputActions={<CopyButton value={output} />}
      input={
        <div className="space-y-3">
          <Textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder={
              mode === "escape"
                ? "Paste HTML or text to escape..."
                : "Paste escaped HTML entities..."
            }
            className="min-h-72"
          />
          <ErrorMessage message={error} />
          <Button type="button" onClick={process}>
            <Play className="h-4 w-4" />
            {mode === "escape" ? "Escape HTML" : "Unescape HTML"}
          </Button>
        </div>
      }
      output={<CodeOutput value={output} placeholder="Converted HTML will appear here." />}
    />
  );
}

function stringifyJwt(decoded: DecodedJwt | null) {
  if (!decoded) {
    return "";
  }

  return [
    "Header",
    JSON.stringify(decoded.header, null, 2),
    "",
    "Payload",
    JSON.stringify(decoded.payload, null, 2),
    "",
    `Signature: ${decoded.signature}`,
  ].join("\n");
}

export function JwtDecoderTool() {
  const [token, setToken] = useState("");
  const [decoded, setDecoded] = useState<DecodedJwt | null>(null);
  const [error, setError] = useState("");
  const copyValue = useMemo(() => stringifyJwt(decoded), [decoded]);

  const process = () => {
    const result = decodeJwt(token);
    setDecoded(result.ok ? result.value : null);
    setError(result.ok ? "" : result.error);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
        <p>Signature is not verified. This decoder only reads the header and payload locally.</p>
      </div>
      <InputOutputPanel
        inputTitle="JWT"
        outputTitle="Decoded token"
        inputActions={
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setToken(jwtExample)}
            >
              <Sparkles className="h-4 w-4" />
              Example
            </Button>
            <ClearButton
              onClear={() => {
                setToken("");
                setDecoded(null);
                setError("");
              }}
            />
          </>
        }
        outputActions={<CopyButton value={copyValue} />}
        input={
          <div className="space-y-3">
            <Textarea
              value={token}
              onChange={(event) => setToken(event.target.value)}
              placeholder="Paste a JWT..."
              className="min-h-72 break-all"
            />
            <ErrorMessage message={error} />
            <Button type="button" onClick={process}>
              <Play className="h-4 w-4" />
              Decode JWT
            </Button>
          </div>
        }
        output={
          decoded ? (
            <div className="space-y-4">
              <div>
                <h3 className="mb-2 text-sm font-semibold text-zinc-700">Header</h3>
                <CodeOutput value={JSON.stringify(decoded.header, null, 2)} className="min-h-0" />
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold text-zinc-700">Payload</h3>
                <CodeOutput value={JSON.stringify(decoded.payload, null, 2)} className="min-h-0" />
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold text-zinc-700">Signature</h3>
                <CodeOutput value={decoded.signature} className="min-h-0" />
              </div>
            </div>
          ) : (
            <CodeOutput placeholder="Decoded header and payload will appear here." />
          )
        }
      />
    </div>
  );
}

export function UuidGeneratorTool() {
  const [quantity, setQuantity] = useState(5);
  const [uuids, setUuids] = useState<string[]>([]);
  const output = uuids.join("\n");

  return (
    <InputOutputPanel
      inputTitle="Options"
      outputTitle="UUIDs"
      inputActions={
        <ClearButton
          onClear={() => {
            setQuantity(5);
            setUuids([]);
          }}
        />
      }
      outputActions={<CopyButton value={output} label="Copy all" />}
      input={
        <div className="max-w-sm space-y-4">
          <div className="space-y-2">
            <Label htmlFor="uuid-quantity">Quantity</Label>
            <Input
              id="uuid-quantity"
              type="number"
              min={1}
              max={100}
              value={quantity}
              onChange={(event) => setQuantity(Number(event.target.value))}
            />
            <p className="text-sm text-zinc-500">Generate between 1 and 100 v4 UUIDs.</p>
          </div>
          <Button type="button" onClick={() => setUuids(generateUuidBatch(quantity))}>
            <Sparkles className="h-4 w-4" />
            Generate UUIDs
          </Button>
        </div>
      }
      output={<CodeOutput value={output} placeholder="Generated UUIDs will appear here." />}
    />
  );
}

export function HashGeneratorTool() {
  const [input, setInput] = useState("");
  const [hashes, setHashes] = useState<HashOutput[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const output = hashes.map((item) => `${item.algorithm}: ${item.value}`).join("\n");

  const process = async () => {
    setBusy(true);
    setError("");

    try {
      setHashes(await generateHashes(input));
    } catch {
      setHashes([]);
      setError("Web Crypto is not available in this browser context.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <InputOutputPanel
      inputTitle="Text to hash"
      outputTitle="Hashes"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setInput("LocalTools privacy-first MVP")}
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton
            onClear={() => {
              setInput("");
              setHashes([]);
              setError("");
            }}
          />
        </>
      }
      outputActions={<CopyButton value={output} />}
      input={
        <div className="space-y-3">
          <Textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Enter text to hash..."
            className="min-h-72"
          />
          <ErrorMessage message={error} />
          <Button type="button" onClick={process} disabled={busy}>
            <Play className="h-4 w-4" />
            {busy ? "Hashing..." : "Generate hashes"}
          </Button>
        </div>
      }
      output={
        hashes.length ? (
          <div className="space-y-3">
            {hashes.map((item) => (
              <div key={item.algorithm}>
                <h3 className="mb-1 text-sm font-semibold text-zinc-700">{item.algorithm}</h3>
                <CodeOutput value={item.value} className="min-h-0" />
              </div>
            ))}
          </div>
        ) : (
          <CodeOutput placeholder="SHA hashes will appear here." />
        )
      }
    />
  );
}

export function UrlEncoderTool() {
  const [action, setAction] = useState<UrlEncodingAction>("encode");
  const [mode, setMode] = useState<UrlEncodingMode>("component");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");

  const process = () => {
    if (action === "encode") {
      setOutput(encodeUrlText(input, mode));
      setError("");
      return;
    }

    const result = decodeUrlText(input, mode);
    setOutput(result.ok ? result.value : "");
    setError(result.ok ? "" : result.error);
  };

  return (
    <InputOutputPanel
      inputTitle="URL text"
      outputTitle={action === "encode" ? "Encoded output" : "Decoded output"}
      inputActions={
        <>
          <div className="inline-flex rounded-md border border-zinc-200 bg-white p-1">
            {(["encode", "decode"] as const).map((option) => (
              <Button
                key={option}
                type="button"
                size="sm"
                variant={action === option ? "default" : "ghost"}
                onClick={() => {
                  setAction(option);
                  setOutput("");
                  setError("");
                }}
              >
                {option === "encode" ? "Encode" : "Decode"}
              </Button>
            ))}
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setInput(getUrlEncodingExample(action, mode))}
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton
            onClear={() => {
              setInput("");
              setOutput("");
              setError("");
            }}
          />
        </>
      }
      outputActions={<CopyButton value={output} />}
      input={
        <div className="space-y-4">
          <div className="inline-flex rounded-md border border-zinc-200 bg-white p-1">
            {([
              ["component", "Component"],
              ["full-url", "Full URL"],
            ] as const).map(([value, label]) => (
              <Button
                key={value}
                type="button"
                size="sm"
                variant={mode === value ? "default" : "ghost"}
                onClick={() => {
                  setMode(value);
                  setOutput("");
                  setError("");
                }}
              >
                {label}
              </Button>
            ))}
          </div>
          <Textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder={
              action === "encode"
                ? "Enter text or a URL to percent-encode..."
                : "Paste percent-encoded text..."
            }
            className="min-h-72"
          />
          <ErrorMessage message={error} />
          <Button type="button" onClick={process}>
            <Play className="h-4 w-4" />
            {action === "encode" ? "Encode" : "Decode"}
          </Button>
        </div>
      }
      output={<CodeOutput value={output} />}
    />
  );
}

function UrlFieldTable({ parsed }: { parsed: ParsedUrl }) {
  const fields: Array<[string, string]> = [
    ["href", parsed.href],
    ["origin", parsed.origin],
    ["protocol", parsed.protocol],
    ["hostname", parsed.hostname],
    ["port", parsed.port || "—"],
    ["pathname", parsed.pathname || "—"],
    ["search", parsed.search || "—"],
    ["hash", parsed.hash || "—"],
  ];

  return (
    <div className="overflow-hidden rounded-md border border-zinc-200">
      <table className="w-full text-left text-sm">
        <tbody>
          {fields.map(([label, value]) => (
            <tr key={label} className="border-b border-zinc-100 last:border-0">
              <th className="w-32 bg-zinc-50 px-3 py-2 font-medium text-zinc-600">{label}</th>
              <td className="break-all px-3 py-2 font-mono text-zinc-900">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function getColorFields(color: ColorConversion): Array<[string, string]> {
  return [
    ["HEX", color.hex],
    ["HEX with alpha", color.hexa],
    ["RGB", color.rgb],
    ["RGBA", color.rgba],
    ["HSL", color.hsl],
    ["HSLA", color.hsla],
    ["Red", color.red.toString()],
    ["Green", color.green.toString()],
    ["Blue", color.blue.toString()],
    ["Alpha", color.alpha.toString()],
  ];
}

function ColorOutputTable({ color }: { color: ColorConversion }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 rounded-md border border-zinc-200 bg-zinc-50 p-3">
        <span
          className="h-14 w-14 flex-none rounded-md border border-zinc-300"
          style={{ backgroundColor: color.rgba }}
        />
        <div className="min-w-0">
          <p className="font-mono text-sm font-semibold text-zinc-950">{color.hex}</p>
          <p className="mt-1 break-all font-mono text-xs text-zinc-500">{color.rgba}</p>
        </div>
      </div>
      <div className="overflow-hidden rounded-md border border-zinc-200">
        <table className="w-full text-left text-sm">
          <tbody>
            {getColorFields(color).map(([label, value]) => (
              <tr key={label} className="border-b border-zinc-100 last:border-0">
                <th className="w-36 bg-zinc-50 px-3 py-2 font-medium text-zinc-600">{label}</th>
                <td className="break-all px-3 py-2 font-mono text-zinc-900">{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function stringifyColorConversion(color: ColorConversion) {
  return getColorFields(color)
    .map(([label, value]) => `${label}: ${value}`)
    .join("\n");
}

export function ColorConverterTool() {
  const [input, setInput] = useState("");
  const [color, setColor] = useState<ColorConversion | null>(null);
  const [error, setError] = useState("");
  const copyValue = useMemo(() => (color ? stringifyColorConversion(color) : ""), [color]);

  const process = () => {
    const result = convertColor(input);
    setColor(result.ok ? result.value : null);
    setError(result.ok ? "" : result.error);
  };

  return (
    <InputOutputPanel
      inputTitle="Color"
      outputTitle="Converted color"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setInput("#38bdf8cc");
              setColor(null);
              setError("");
            }}
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton
            onClear={() => {
              setInput("");
              setColor(null);
              setError("");
            }}
          />
        </>
      }
      outputActions={<CopyButton value={copyValue} label="Copy all" />}
      input={
        <div className="max-w-xl space-y-3">
          <div className="space-y-2">
            <Label htmlFor="color-input">HEX, RGB, or RGBA</Label>
            <Input
              id="color-input"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="#2563eb or rgba(37, 99, 235, 0.8)"
            />
            <p className="text-sm text-zinc-500">
              Supports 3, 4, 6, and 8 digit HEX plus comma-separated rgb() and rgba().
            </p>
          </div>
          <ErrorMessage message={error} />
          <Button type="button" onClick={process}>
            <Palette className="h-4 w-4" />
            Convert color
          </Button>
        </div>
      }
      output={
        color ? (
          <ColorOutputTable color={color} />
        ) : (
          <CodeOutput placeholder="Converted color formats will appear here." />
        )
      }
    />
  );
}


export function PasswordGeneratorTool() {
  const [length, setLength] = useState(16);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeDigits, setIncludeDigits] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(false);
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");

  const process = () => {
    const result = generatePassword({
      length,
      includeUppercase,
      includeLowercase,
      includeDigits,
      includeSymbols,
    });
    setOutput(result.ok ? result.value : "");
    setError(result.ok ? "" : result.error);
  };

  return (
    <InputOutputPanel
      inputTitle="Password options"
      outputTitle="Generated password"
      inputActions={<ClearButton onClear={() => { setOutput(""); setError(""); }} />}
      outputActions={<CopyButton value={output} />}
      input={
        <div className="max-w-md space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password-length">Length</Label>
            <Input id="password-length" type="number" min={4} max={128} value={length} onChange={(event) => setLength(Number(event.target.value))} />
          </div>
          {[
            [includeLowercase, setIncludeLowercase, "Lowercase"],
            [includeUppercase, setIncludeUppercase, "Uppercase"],
            [includeDigits, setIncludeDigits, "Digits"],
            [includeSymbols, setIncludeSymbols, "Symbols"],
          ].map(([enabled, setter, label]) => (
            <label key={String(label)} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={Boolean(enabled)} onChange={(event) => (setter as (value: boolean) => void)(event.target.checked)} />
              <span>{String(label)}</span>
            </label>
          ))}
          <ErrorMessage message={error} />
          <Button type="button" onClick={process}>Generate password</Button>
        </div>
      }
      output={<CodeOutput value={output} placeholder="A random password will appear here." />}
    />
  );
}

export function UrlParserTool() {
  const [input, setInput] = useState("");
  const [parsed, setParsed] = useState<ParsedUrl | null>(null);
  const [error, setError] = useState("");
  const output = parsed ? JSON.stringify(parsed, null, 2) : "";

  const process = () => {
    const result = parseUrl(input);
    setParsed(result.ok ? result.value : null);
    setError(result.ok ? "" : result.error);
  };

  return (
    <InputOutputPanel
      inputTitle="URL"
      outputTitle="Parsed URL"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              setInput("https://example.com/products/toolbox?utm_source=newsletter&plan=pro#pricing")
            }
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton
            onClear={() => {
              setInput("");
              setParsed(null);
              setError("");
            }}
          />
        </>
      }
      outputActions={<CopyButton value={output} />}
      input={
        <div className="space-y-3">
          <Input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="https://example.com/path?key=value#section"
          />
          <ErrorMessage message={error} />
          <Button type="button" onClick={process}>
            <Rows3 className="h-4 w-4" />
            Parse URL
          </Button>
        </div>
      }
      output={
        parsed ? (
          <div className="space-y-4">
            <UrlFieldTable parsed={parsed} />
            <div>
              <h3 className="mb-2 text-sm font-semibold text-zinc-700">Query parameters</h3>
              {parsed.queryParams.length ? (
                <div className="overflow-hidden rounded-md border border-zinc-200">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-zinc-50 text-zinc-600">
                      <tr>
                        <th className="px-3 py-2 font-medium">Key</th>
                        <th className="px-3 py-2 font-medium">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.queryParams.map((param, index) => (
                        <tr key={`${param.key}-${index}`} className="border-t border-zinc-100">
                          <td className="break-all px-3 py-2 font-mono">{param.key}</td>
                          <td className="break-all px-3 py-2 font-mono">{param.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-4 text-sm text-zinc-500">
                  No query parameters found.
                </p>
              )}
            </div>
          </div>
        ) : (
          <CodeOutput placeholder="Parsed fields and query parameters will appear here." />
        )
      }
    />
  );
}

export function DeveloperToolPlaceholder({ label }: { label: string }) {
  return (
    <div className={cn("rounded-md border border-dashed border-zinc-300 p-6 text-sm text-zinc-500")}>
      {label}
    </div>
  );
}
